import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
// import { InAppPurchase2 } from '@awesome-cordova-plugins/in-app-purchase-2'; // Not used if using window.CdvPurchase directly
import { useAuth } from '../contexts/AuthContext';

const useGooglePlay = () => {
    const { refreshUser } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    // Product IDs must match Google Play Console
    const PRODUCT_IDS = ['starter_monthly', 'pro_monthly'];

    useEffect(() => {
        if (Capacitor.getPlatform() === 'web') {
            setLoading(false);
            return;
        }

        const onDeviceReady = () => {
            console.log("📱 Device Ready fired. Initializing IAP...");
            initializeIAP();
        };

        if (document.readyState === 'loading') {
            document.addEventListener('deviceready', onDeviceReady);
        } else {
            onDeviceReady();
        }

        return () => {
            document.removeEventListener('deviceready', onDeviceReady);
        };
    }, []);

    const initializeIAP = async () => {
        // Support both v13 `CdvPurchase` and older `window.store` instances
        let store;
        let ProductType;
        let Platform;
        let LogLevel;

        if (window.CdvPurchase) {
            ({ store, ProductType, Platform, LogLevel } = window.CdvPurchase);
            console.log('Detected window.CdvPurchase (v13+)');
        } else if (window.store) {
            // Older plugin exposes `window.store` directly
            store = window.store;
            ProductType = store; // constants like PAID_SUBSCRIPTION live on store
            Platform = { GOOGLE_PLAY: 'google_play' };
            LogLevel = { DEBUG: 'debug' };
            console.log('Detected legacy window.store instance');
        } else {
            console.warn('⚠️ Billing plugin not available (window.CdvPurchase or window.store).');
            setLoading(false);
            return;
        }

        if (!store) {
            console.error('❌ store object is undefined');
            setLoading(false);
            return;
        }

        // 1. Verbosity (best-effort)
        try {
            if (LogLevel && store.verbosity !== undefined) store.verbosity = LogLevel.DEBUG;
            console.log("🔧 Plugin Verbosity set to DEBUG");
        } catch (e) {
            // ignore if not supported
        }

        let initOk = false;

        try {
            // 2. Constants Debug
            console.log(`🔧 Constants: PAID_SUBSCRIPTION=${ProductType.PAID_SUBSCRIPTION}, GOOGLE_PLAY=${Platform.GOOGLE_PLAY}`);

            // 3. Register Batch
            const productsToRegister = PRODUCT_IDS.map(id => ({
                id: id,
                type: ProductType && ProductType.PAID_SUBSCRIPTION ? ProductType.PAID_SUBSCRIPTION : (store.PAID_SUBSCRIPTION || 'subs'),
            }));

            console.log("📝 Registering batch:", JSON.stringify(productsToRegister));

            // Register call (works for both APIs)
            try {
                store.register(productsToRegister);
            } catch (e) {
                console.warn('Register failed, continuing (maybe already registered)', e);
            }

            // 4. Verification
            console.log("📋 Checking registry immediately...");
            PRODUCT_IDS.forEach(id => {
                const p = store.get(id);
                console.log(`   > store.get('${id}'):`, p ? 'FOUND' : 'NOT FOUND');
            });

            // 5. Setup Listeners
            store.when('product').updated((product) => {
                console.log(`📦 Product updated: ${product.id} [${product.state}]`);
                setProducts(prev => {
                    const others = prev.filter(p => p.id !== product.id);
                    return [...others, product];
                });
            });

            store.when('approved', (product) => {
                console.log(`✔️ Purchase approved: ${product.id}`);
                // validatePurchase(product); // keep simple for now
                product.verify();
            });

            store.when('verified', (product) => {
                console.log(`✓ Purchase verified: ${product.id}`);
                product.finish();
                refreshUser();
                alert('🎉 Subscription activated successfully!');
            });

            store.when('error', (error) => {
                console.error('❌ Store error:', JSON.stringify(error));
            });

            // 6. Initialize / Refresh (use whatever API is available)
            console.log("🔄 Initializing / refreshing store...");

            const timeoutMs = 8000;
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error(`Store init timed out after ${timeoutMs}ms`)), timeoutMs);
            });

            const initPromise = (async () => {
                if (typeof store.initialize === 'function') {
                    // v13 style
                    try {
                        await store.initialize(Platform && Platform.GOOGLE_PLAY ? [Platform.GOOGLE_PLAY] : undefined);
                        return 'initialized';
                    } catch (e) {
                        throw e;
                    }
                }

                // Fallback: older plugin uses refresh()
                if (typeof store.refresh === 'function') {
                    try {
                        await new Promise((resolve, reject) => {
                            store.refresh();
                            // The older store triggers `ready` event — poll briefly
                            const start = Date.now();
                            const check = () => {
                                if (store.products && ((Array.isArray(store.products) && store.products.length > 0) || Object.keys(store.products || {}).length > 0)) return resolve('refreshed');
                                if (Date.now() - start > 4000) return resolve('refreshed-timeout');
                                setTimeout(check, 200);
                            };
                            check();
                        });
                        return 'refreshed';
                    } catch (e) {
                        throw e;
                    }
                }

                // If neither exists, resolve immediately
                return 'noop';
            })();

            initOk = false;
            try {
                await Promise.race([initPromise, timeoutPromise]);
                initOk = true;
                console.log("✅ store initialization/refresh completed.");
            } catch (initErr) {
                console.error('❌ Store init/refresh failed:', initErr);
                // Specific helpful message for Play Store billing version issue
                try {
                    const msg = initErr && initErr.message ? initErr.message : String(initErr);
                    if (msg.includes('BILLING_UNAVAILABLE') || msg.includes('less than 3')) {
                        console.error('⚠️ Play Store on this device does not support Billing API v3+.');
                        alert('Google Play Billing is unavailable on this device (Billing API < v3).\nUse a device or emulator with Google Play support, or install via Play Store (internal testing).');
                    }
                } catch (e) { /* ignore */ }
            }

            // Extra debug: log available store keys and products for diagnostics
            try {
                if (store && typeof Object.keys === 'function') {
                    console.log('🔎 store keys:', Object.keys(store));
                }
                if (store && store.products) {
                    try {
                        const ids = Array.isArray(store.products)
                            ? store.products.map(p => p.id)
                            : Object.values(store.products || {}).map(p => p.id);
                        console.log('🔎 Available products after init:', ids.join(', '));
                    } catch (e) {
                        console.log('🔎 Unable to enumerate store.products', e);
                    }
                } else {
                    console.log('🔎 store.products is empty or undefined after init');
                }
            } catch (e) {
                console.warn('🔎 Debug listing failed', e);
            }

        } catch (err) {
            console.error('❌ Exception in initializeIAP:', err);
        } finally {
            setIsInitialized(!!(typeof initOk !== 'undefined' && initOk));
            setLoading(false);
        }
    };

    const validatePurchase = async (product) => {
        // Here you would normally send the receipt to your backend
        // For now, we trust the device (local verification)
        console.log(`Testing validation for ${product.id}`);
        product.verify();
    };

    const subscribe = (productId) => {
        const isV13 = !!window.CdvPurchase;
        const store = isV13 ? window.CdvPurchase.store : window.store;

        console.log(`🛒 Attempting to subscribe to: ${productId}`);

        if (Capacitor.getPlatform() === 'web') {
            alert('In-App Purchase not available on web');
            return;
        }

        if (!store) {
            console.error('❌ Store not available');
            alert('Billing system not ready. Please restart the app.');
            return;
        }

        // Try to get the product. In v13, get() returns a Product object.
        let product = store.get(productId);

        // If not found, try looking it up in the products array manually (fallback)
        if (!product && store.products) {
            // Handle if store.products is an array or a Map-like object
            if (Array.isArray(store.products)) {
                product = store.products.find(p => p.id === productId);
            } else if (typeof store.products === 'object') {
                // Try to find in values if it's a map
                product = Object.values(store.products).find(p => p.id === productId);
            }
        }

        if (product) {
            if (product.canPurchase) {
                store.order(productId);
            } else {
                console.log(`Product ${productId} cannot be purchased. State: ${product.state}`);
                // Try ordering anyway in case 'canPurchase' logic is strict but user wants to retry
                store.order(productId);
            }
        } else {
            // Detailed Debug info
            let productsDump = 'unknown kind';
            if (store.products) {
                if (Array.isArray(store.products)) {
                    productsDump = store.products.map(p => `${p.id} (${p.state})`).join(', ');
                } else {
                    try {
                        productsDump = JSON.stringify(store.products);
                    } catch (e) { productsDump = 'Unserializable Object'; }
                }
            } else {
                productsDump = 'NULL (store.products is undefined)';
            }

            console.error(`Product '${productId}' not found in store registry.`);
            console.error(`Registry dump: ${productsDump}`);

            alert(`Product '${productId}' not found.\n\nRegistry: ${productsDump}\n\nPlease check logs for details.`);
        }
    };

    return {
        products,
        subscribe,
        loading,
        isInitialized
    };
};

export default useGooglePlay;
